import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Search, Filter, Calendar, Plane } from 'lucide-react';
import { useToast } from '../components/Toast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const ADMIN_KEY = process.env.REACT_APP_ADMIN_KEY;

const TravelArchivePage = () => {
  const navigate = useNavigate();
  const { success, error: showError } = useToast();
  
  const [archivedItems, setArchivedItems] = useState([]);
  const [assistants, setAssistants] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Filters
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedAssistant, setSelectedAssistant] = useState('');
  const [selectedType, setSelectedType] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      // Fetch assistants for filter
      const assistRes = await fetch(`${BACKEND_URL}/api/admin/assistants`, {
        headers: { 'x-admin-key': ADMIN_KEY }
      });
      if (assistRes.ok) {
        const assistData = await assistRes.json();
        setAssistants(assistData.assistants || []);
      }

      // Fetch archived travel items
      const archiveRes = await fetch(`${BACKEND_URL}/api/admin/travel/archive`, {
        headers: { 'x-admin-key': ADMIN_KEY }
      });
      if (archiveRes.ok) {
        const archiveData = await archiveRes.json();
        setArchivedItems(archiveData.archived_items || []);
      }
    } catch (err) {
      console.error('Error fetching data:', err);
      showError('Failed to load archive', 'Please refresh the page.');
    } finally {
      setLoading(false);
    }
  };

  const handleUnarchive = async (itemId) => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/travel/${itemId}/unarchive`, {
        method: 'PATCH',
        headers: { 'x-admin-key': ADMIN_KEY }
      });

      if (res.ok) {
        success('Item unarchived', 'Restored to active travel items.');
        fetchData();
      } else {
        showError('Failed to unarchive', 'Please try again.');
      }
    } catch (err) {
      showError('Network error', 'Please try again.');
    }
  };

  const filteredItems = archivedItems.filter(item => {
    // Search filter
    if (searchQuery && !item.trip_name?.toLowerCase().includes(searchQuery.toLowerCase()) &&
        !item.vendor?.toLowerCase().includes(searchQuery.toLowerCase()) &&
        !item.confirmation?.toLowerCase().includes(searchQuery.toLowerCase())) {
      return false;
    }
    
    // Assistant filter
    if (selectedAssistant && item.assistant_id !== selectedAssistant) {
      return false;
    }
    
    // Type filter
    if (selectedType && item.type !== selectedType) {
      return false;
    }
    
    // Date range filter
    if (dateFrom && item.travel_date && new Date(item.travel_date) < new Date(dateFrom)) {
      return false;
    }
    if (dateTo && item.travel_date && new Date(item.travel_date) > new Date(dateTo)) {
      return false;
    }
    
    return true;
  });

  return (
    <div style={{ minHeight: '100vh', background: '#F8F9FA' }}>
      {/* Header */}
      <div style={{ background: '#FFFFFF', borderBottom: '1px solid #E5E7EB', padding: '20px 32px' }}>
        <div style={{ maxWidth: '1400px', margin: '0 auto', display: 'flex', alignItems: 'center', gap: '16px' }}>
          <button
            onClick={() => navigate('/admin/ops')}
            style={{
              padding: '8px',
              background: 'transparent',
              border: '1px solid #E5E7EB',
              borderRadius: '8px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center'
            }}
          >
            <ArrowLeft size={18} color="#6B7280" />
          </button>
          <div style={{ flex: 1 }}>
            <h1 style={{ color: '#0A0A0A', fontSize: '24px', fontWeight: '600', margin: '0 0 4px 0' }}>
              Travel Archive
            </h1>
            <p style={{ color: '#6B7280', fontSize: '14px', margin: 0 }}>
              Archived items are hidden from active views but remain searchable.
            </p>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div style={{ maxWidth: '1400px', margin: '0 auto', padding: '24px 32px' }}>
        <div style={{ background: '#FFFFFF', borderRadius: '12px', border: '1px solid #E5E7EB', padding: '24px', marginBottom: '24px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px' }}>
            <Filter size={18} color="#6B7280" />
            <h3 style={{ color: '#0A0A0A', fontSize: '16px', fontWeight: '600', margin: 0 }}>Filters</h3>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px' }}>
            {/* Search */}
            <div>
              <label style={{ display: 'block', color: '#374151', fontSize: '13px', fontWeight: '500', marginBottom: '6px' }}>
                Search
              </label>
              <div style={{ position: 'relative' }}>
                <Search size={16} color="#9CA3AF" style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)' }} />
                <input
                  type="text"
                  placeholder="Trip, vendor, confirmation..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '8px 12px 8px 34px',
                    border: '1px solid #D1D5DB',
                    borderRadius: '6px',
                    fontSize: '14px'
                  }}
                />
              </div>
            </div>

            {/* Assistant Filter */}
            <div>
              <label style={{ display: 'block', color: '#374151', fontSize: '13px', fontWeight: '500', marginBottom: '6px' }}>
                Assistant
              </label>
              <select
                value={selectedAssistant}
                onChange={(e) => setSelectedAssistant(e.target.value)}
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: '1px solid #D1D5DB',
                  borderRadius: '6px',
                  fontSize: '14px'
                }}
              >
                <option value="">All Assistants</option>
                {assistants.map(a => (
                  <option key={a.id} value={a.id}>{a.full_name}</option>
                ))}
              </select>
            </div>

            {/* Type Filter */}
            <div>
              <label style={{ display: 'block', color: '#374151', fontSize: '13px', fontWeight: '500', marginBottom: '6px' }}>
                Type
              </label>
              <select
                value={selectedType}
                onChange={(e) => setSelectedType(e.target.value)}
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: '1px solid #D1D5DB',
                  borderRadius: '6px',
                  fontSize: '14px'
                }}
              >
                <option value="">All Types</option>
                <option value="flight">Flight</option>
                <option value="hotel">Hotel</option>
                <option value="car">Car Rental</option>
                <option value="itinerary">Itinerary</option>
                <option value="receipt">Receipt</option>
                <option value="note">Note</option>
              </select>
            </div>

            {/* Date From */}
            <div>
              <label style={{ display: 'block', color: '#374151', fontSize: '13px', fontWeight: '500', marginBottom: '6px' }}>
                Date From
              </label>
              <input
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: '1px solid #D1D5DB',
                  borderRadius: '6px',
                  fontSize: '14px'
                }}
              />
            </div>

            {/* Date To */}
            <div>
              <label style={{ display: 'block', color: '#374151', fontSize: '13px', fontWeight: '500', marginBottom: '6px' }}>
                Date To
              </label>
              <input
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: '1px solid #D1D5DB',
                  borderRadius: '6px',
                  fontSize: '14px'
                }}
              />
            </div>
          </div>

          {/* Clear Filters */}
          {(searchQuery || selectedAssistant || selectedType || dateFrom || dateTo) && (
            <button
              onClick={() => {
                setSearchQuery('');
                setSelectedAssistant('');
                setSelectedType('');
                setDateFrom('');
                setDateTo('');
              }}
              style={{
                marginTop: '16px',
                padding: '6px 12px',
                background: 'transparent',
                color: '#6B7280',
                border: '1px solid #E5E7EB',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '13px'
              }}
            >
              Clear All Filters
            </button>
          )}
        </div>

        {/* Results */}
        <div style={{ background: '#FFFFFF', borderRadius: '12px', border: '1px solid #E5E7EB', overflow: 'hidden' }}>
          <div style={{ padding: '16px 24px', borderBottom: '1px solid #E5E7EB', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <h3 style={{ color: '#0A0A0A', fontSize: '16px', fontWeight: '600', margin: 0 }}>
              Archived Items ({filteredItems.length})
            </h3>
          </div>

          {loading ? (
            <div style={{ padding: '60px', textAlign: 'center' }}>
              <p style={{ color: '#6B7280', fontSize: '14px' }}>Loading archived items...</p>
            </div>
          ) : filteredItems.length === 0 ? (
            <div style={{ padding: '60px', textAlign: 'center' }}>
              <Plane size={40} color="#E5E7EB" style={{ margin: '0 auto 12px' }} />
              <p style={{ color: '#6B7280', fontSize: '14px' }}>
                {archivedItems.length === 0 
                  ? 'No archived items yet'
                  : 'No items match your filters'}
              </p>
            </div>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead style={{ background: '#F9FAFB' }}>
                <tr>
                  <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#6B7280', textTransform: 'uppercase' }}>Trip</th>
                  <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#6B7280', textTransform: 'uppercase' }}>Type</th>
                  <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#6B7280', textTransform: 'uppercase' }}>Assistant</th>
                  <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#6B7280', textTransform: 'uppercase' }}>Travel Date</th>
                  <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#6B7280', textTransform: 'uppercase' }}>Archived</th>
                  <th style={{ padding: '12px 16px', textAlign: 'right', fontSize: '12px', fontWeight: '600', color: '#6B7280', textTransform: 'uppercase' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredItems.map((item, idx) => (
                  <tr key={item.id} style={{ borderTop: idx > 0 ? '1px solid #F3F4F6' : 'none' }}>
                    <td style={{ padding: '16px' }}>
                      <p style={{ color: '#0A0A0A', fontSize: '14px', fontWeight: '500', margin: '0 0 4px 0' }}>
                        {item.trip_name}
                      </p>
                      {item.vendor && (
                        <p style={{ color: '#6B7280', fontSize: '12px', margin: 0 }}>
                          {item.vendor} {item.confirmation && `• ${item.confirmation}`}
                        </p>
                      )}
                    </td>
                    <td style={{ padding: '16px' }}>
                      <span style={{ color: '#374151', fontSize: '13px', textTransform: 'capitalize' }}>
                        {item.type}
                      </span>
                    </td>
                    <td style={{ padding: '16px' }}>
                      <span style={{ color: '#6B7280', fontSize: '13px' }}>
                        {assistants.find(a => a.id === item.assistant_id)?.full_name || 'Unknown'}
                      </span>
                    </td>
                    <td style={{ padding: '16px' }}>
                      <span style={{ color: '#6B7280', fontSize: '13px' }}>
                        {item.travel_date ? new Date(item.travel_date).toLocaleDateString() : '—'}
                      </span>
                    </td>
                    <td style={{ padding: '16px' }}>
                      <span style={{ color: '#9CA3AF', fontSize: '12px' }}>
                        {item.archived_at ? new Date(item.archived_at).toLocaleDateString() : '—'}
                      </span>
                    </td>
                    <td style={{ padding: '16px', textAlign: 'right' }}>
                      <button
                        onClick={() => handleUnarchive(item.id)}
                        style={{
                          padding: '6px 12px',
                          background: '#10B981',
                          color: '#FFFFFF',
                          border: 'none',
                          borderRadius: '6px',
                          cursor: 'pointer',
                          fontSize: '12px',
                          fontWeight: '500'
                        }}
                      >
                        Unarchive
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
};

export default TravelArchivePage;
